import {memo, useCallback, useEffect, useState} from 'react';
import {Modal, Portal} from 'react-native-paper';
import {StyleSheet, TextProps, View} from 'react-native';
import Styles from '../../../Styles';
import Localized from '../../../../constants/AppStrings';
import CancelIcon from '../../../img/svg/CancelIcon';
import AVTouchableOpacity from '../../AVTouchableOpacity';
import AVText from '../../AVText';
import {scanAndPayTemplate} from '../../../screens/scanAndPay/templates/scanandPayThemeHelper';
import RoundedButton, {ButtonType} from '../../RoundedButton';
import {getDescriber} from '../../../screens/orderAhead/descriptor/DescriptorType';
import {Linking} from 'react-native';
import FallbackImage from '../../../img/FallbackImage';
import MockFullscreenAnnouncementIcon from '../../../img/svg/MockFullscreenAnnouncmentIcon';
import {getLayoutDescriber} from 'src/components/elements/layoutDescriptor/DescriptorType';

interface AnnouncementsPopUpProps {
  isModalVisible: boolean;
  onModalClose?: () => void;
  title?: string;
  body?: string;
  btnText?: string;
  btnLink?: string;
  coverImg?: string;
}

const {announcementPopUpButtonColor} = getDescriber();
const buttonLabelAdditionalProps: TextProps = {
  numberOfLines: 1,
  ellipsizeMode: 'tail',
};

export const AnnouncementsPopUp = memo(
  ({
    isModalVisible,
    onModalClose,
    title,
    coverImg,
    body,
    btnLink,
    btnText,
  }: AnnouncementsPopUpProps) => {
    const isOnlyImage = !title && !body && !btnText;
    const isOnlyTextContent = title && body && !btnText && !coverImg;
    const isWithoutImage = !coverImg;
    const isLongTextContent = title?.length + body?.length > 100;
    const isHugeContent =
      title?.length + body?.length > 300 && btnText && coverImg;

    const [isLinkCorrect, setIsLinkCorrect] = useState(false);

    useEffect(() => {
      if (btnLink) {
        Linking.canOpenURL(btnLink).then((supported) => {
          setIsLinkCorrect(supported);
        });
      }
    }, [btnLink]);

    const onPressActionButton = useCallback(async () => {
      if (isLinkCorrect) {
        onModalClose();
        await Linking.openURL(btnLink);
      } else {
        return;
      }
    }, [isLinkCorrect, onModalClose, btnLink]);

    return (
      <Portal>
        <Modal
          visible={isModalVisible}
          onDismiss={onModalClose}
          style={styles.modalContainer}
        >
          <View
            style={[
              styles.modalContent,
              isWithoutImage &&
                !isLongTextContent &&
                styles.noImageModalContent,
              isHugeContent && styles.hugeContent,
            ]}
          >
            <AVTouchableOpacity
              accessible
              accessibilityLabel={Localized.Buttons.cancel}
              accessibilityRole="button"
              aria-label={Localized.Buttons.cancel}
              role="button"
              style={[
                styles.cancelButtonContainer,
                isOnlyImage && styles.onlyImageCancelArrowContainer,
              ]}
              onPress={onModalClose}
              hitSlop={Styles.HitSlop_10}
            >
              <CancelIcon />
            </AVTouchableOpacity>

            <View
              style={[
                styles.contentContainer,
                (isOnlyImage || isOnlyTextContent) && styles.onlyImage,
              ]}
            >
              {coverImg && (
                <FallbackImage
                  uri={coverImg}
                  fallback={
                    <MockFullscreenAnnouncementIcon
                      isWithText={!title?.length && !body?.length}
                    />
                  }
                  style={
                    isOnlyImage
                      ? styles.backgroundImageFulfill
                      : styles.backgroundImage
                  }
                  resizeMode="cover"
                  accessible
                  accessibilityLabel="announcement_icon"
                  accessibilityRole="image"
                  role="img"
                  aria-label="announcements_icon"
                />
              )}
              <View>
                {title ? (
                  <AVText
                    style={styles.titleContainer}
                    accessible
                    maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm0}
                    accessibilityLabel={title}
                    aria-label={`${title}Text`}
                    testID={'announcement_popup_title'}
                  >
                    {title}
                  </AVText>
                ) : null}
                {body ? (
                  <AVText
                    style={styles.bodyText}
                    accessible
                    maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm0}
                    accessibilityLabel={body}
                    aria-label={`${body} text`}
                    testID={'announcement_popup_body_text'}
                  >
                    {body}
                  </AVText>
                ) : null}
              </View>
              {btnText && isLinkCorrect ? (
                <RoundedButton
                  testID={'announcement_popup_button'}
                  buttonLabelAdditionalProps={buttonLabelAdditionalProps}
                  buttonType={ButtonType.normal}
                  color={announcementPopUpButtonColor}
                  containerStyle={[styles.buttonStyle]}
                  onPress={onPressActionButton}
                  accessibilityLabel={btnText}
                  text={btnText}
                  maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm2}
                  textStyle={[styles.buttonText]}
                />
              ) : null}
            </View>
          </View>
        </Modal>
      </Portal>
    );
  },
);

const styles = StyleSheet.create({
  modalContainer: {
    flex: 1,
    alignItems: 'center',
    marginRight: 15,
    justifyContent: 'center',
    marginLeft: 15,
  },
  modalContent: {
    width: 340,
    height: 450,
    borderRadius: Styles.Spacing.m3,
    backgroundColor: Styles.white,
    overflow: 'hidden',
  },
  hugeContent: {
    width: 340,
    height: '100%',
    borderRadius: Styles.Spacing.m3,
    backgroundColor: Styles.white,
    overflow: 'hidden',
  },
  noImageModalContent: {
    minHeight: 180,
    maxHeight: 260,
  },
  contentContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: Styles.Heights.h2,
    marginTop: 45,
    flex: 1,
    gap: 24,
  },
  onlyImage: {
    marginTop: 0,
  },
  cancelButtonContainer: {
    position: 'absolute',
    justifyContent: 'flex-end',
    right: 0,
    top: 0,
    zIndex: 99999,
    backgroundColor: Styles.white,
    borderRadius: Styles.Spacing.m4,
    padding: Styles.Heights.h1 - 1,
  },
  onlyImageCancelArrowContainer: {
    right: 6,
    top: 6,
    padding: Styles.Heights.h1 - 5,
  },
  titleContainer: {
    fontFamily: scanAndPayTemplate.textFontFamily,
    color: Styles.black,
    textAlign: 'center',
    fontSize: getLayoutDescriber().headerFontSize(),
    fontWeight: '700',
    marginBottom: Styles.Spacing.m2,
  },
  bodyText: {
    fontFamily: scanAndPayTemplate.textFontFamily,
    color: Styles.black,
    fontSize: getLayoutDescriber().bodyFontSize(),
    fontWeight: '400',
    textAlign: 'center',
    marginBottom: Styles.Spacing.m1,
  },
  backgroundImage: {
    flex: 1,
    width: '100%',
    borderRadius: Styles.Spacing.m3,
    maxHeight: 260,
  },
  backgroundImageFulfill: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: Styles.Spacing.m3,
  },
  buttonStyle: {
    width: 145,
    alignSelf: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: Styles.Spacing.m4,
  },
  buttonText: {
    color: scanAndPayTemplate.okayBtnTextColor,
    fontSize: getLayoutDescriber().bodyFontSize(),
    fontWeight: '700',
    textAlign: 'center',
    fontFamily: scanAndPayTemplate.textFontFamily,
    textTransform: 'none',
  },
});
